import React, { useEffect, useRef } from 'react';
import { founderInfo } from '../data/mock';

export const Founder = () => {
  const founderRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fade-in');
          }
        });
      },
      { threshold: 0.2 }
    );

    if (founderRef.current) {
      observer.observe(founderRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section id="about" className="py-16 sm:py-24 lg:py-32 bg-gradient-to-b from-white via-gray-50 to-white relative overflow-hidden">
      {/* Premium Background Elements */}
      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[1000px] h-[1000px] bg-[#C9A14A]/3 rounded-full blur-3xl" />
      <div className="absolute top-20 right-20 w-64 h-64 bg-[#0A1F3D]/5 rounded-full blur-2xl" />
      <div className="absolute bottom-20 left-20 w-64 h-64 bg-[#C9A14A]/5 rounded-full blur-2xl" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-12 sm:mb-16 lg:mb-20">
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-[#0A1F3D] mb-4 sm:mb-6">
            Meet Our <span className="text-[#C9A14A]">Founder</span>
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-transparent via-[#C9A14A] to-transparent mx-auto" />
        </div>

        {/* Founder Content */}
        <div
          ref={founderRef}
          className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-center opacity-0"
        >
          {/* Image Side with Enhanced Effects */}
          <div className="relative group order-2 lg:order-1">
            <div className="relative rounded-2xl sm:rounded-3xl overflow-hidden shadow-2xl max-w-md mx-auto lg:max-w-none">
              {/* Spotlight Effect */}
              <div className="absolute inset-0 bg-gradient-to-br from-[#C9A14A]/20 via-transparent to-[#0A1F3D]/30 z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
              
              {/* Main Image */}
              <div className="relative overflow-hidden">
                <img
                  src={founderInfo.image}
                  alt={founderInfo.name}
                  className="w-full h-auto transform transition-all duration-700 group-hover:scale-110"
                />
                
                {/* Elegant Bottom Gradient */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A1F3D]/80 via-[#0A1F3D]/20 to-transparent z-10" />
              </div>

              {/* Gold Border Glow on Hover */}
              <div className="absolute inset-0 rounded-3xl border-2 border-transparent group-hover:border-[#C9A14A]/60 transition-all duration-700" />
            </div>

            {/* Decorative Corner Accents */}
            <div className="absolute -top-6 -left-6 w-32 h-32 border-t-4 border-l-4 border-[#C9A14A] rounded-tl-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
            <div className="absolute -bottom-6 -right-6 w-32 h-32 border-b-4 border-r-4 border-[#C9A14A] rounded-br-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
            
            {/* Floating Glow Effect */}
            <div className="absolute -z-10 inset-0 bg-[#C9A14A]/20 blur-3xl rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
          </div>

          {/* Content Side with Premium Typography */}
          <div className="space-y-6 sm:space-y-8 order-1 lg:order-2 text-center lg:text-left">
            <div>
              <h3 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-[#0A1F3D] mb-2 sm:mb-3 leading-tight">
                {founderInfo.name}
              </h3>
              <p className="text-lg sm:text-xl md:text-2xl text-[#C9A14A] font-semibold tracking-wide">
                {founderInfo.title}
              </p>
            </div>

            <div className="h-1 w-24 sm:w-32 bg-gradient-to-r from-[#C9A14A] to-transparent mx-auto lg:mx-0" />

            <p className="text-base sm:text-lg md:text-xl text-gray-700 leading-relaxed font-light px-4 lg:px-0">
              {founderInfo.description}
            </p>

            {/* Premium Stats Cards */}
            <div className="grid grid-cols-2 gap-4 sm:gap-6 pt-6 sm:pt-8 max-w-md mx-auto lg:max-w-none">
              <div className="group relative bg-gradient-to-br from-[#0A1F3D] via-[#0D2847] to-[#0A1F3D] rounded-xl sm:rounded-2xl p-5 sm:p-6 md:p-8 text-center overflow-hidden transition-all duration-500 hover:shadow-2xl hover:scale-105">
                {/* Animated Background Gradient */}
                <div className="absolute inset-0 bg-gradient-to-br from-[#C9A14A]/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                
                <div className="relative z-10">
                  <div className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#C9A14A] mb-2 sm:mb-3">15+</div>
                  <div className="text-xs sm:text-sm text-gray-300 tracking-wider uppercase">Years of Excellence</div>
                </div>
              </div>
              
              <div className="group relative bg-gradient-to-br from-[#0A1F3D] via-[#0D2847] to-[#0A1F3D] rounded-xl sm:rounded-2xl p-5 sm:p-6 md:p-8 text-center overflow-hidden transition-all duration-500 hover:shadow-2xl hover:scale-105">
                {/* Animated Background Gradient */}
                <div className="absolute inset-0 bg-gradient-to-br from-[#C9A14A]/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                
                <div className="relative z-10">
                  <div className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#C9A14A] mb-2 sm:mb-3">500+</div>
                  <div className="text-xs sm:text-sm text-gray-300 tracking-wider uppercase">Satisfied Clients</div>
                </div>
              </div>
            </div>

            {/* Trust Badge */}
            <div className="pt-4 sm:pt-6 flex justify-center lg:justify-start">
              <div className="inline-flex items-center space-x-2 sm:space-x-3 bg-[#C9A14A]/10 px-4 sm:px-6 py-2 sm:py-3 rounded-full">
                <div className="w-2 h-2 sm:w-3 sm:h-3 bg-[#C9A14A] rounded-full animate-pulse" />
                <span className="text-[#0A1F3D] font-semibold text-xs sm:text-sm md:text-base">Trusted by 500+ Happy Families</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
